import React, { useEffect, useState } from 'react';
import axios from '../axios-config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierClarifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async (status) => {
    setLoading(true);
    try {
      const qs = status && status !== 'all' ? `?status=${status}` : '';
      const { data } = await axios.get(`${API}/supplier/clarifications${qs}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast.error('Failed to load clarifications'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(filter); }, [filter]);

  const pendingCount = items.filter(i => !i.answer).length;
  const answeredCount = items.filter(i => i.answer).length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-navy text-white sticky top-0 z-30">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/supplier-dashboard')} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="clr-center-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </button>
          <div className="font-bold text-sm inline-flex items-center"><MessageSquare className="h-4 w-4 mr-1.5 text-gold" /> Clarification Center</div>
          <div className="w-12" />
        </nav>
        <div className="container mx-auto px-4 pb-3 flex gap-1.5 text-xs">
          {[
            { key: 'all', label: `All (${items.length})` },
            { key: 'pending', label: `Pending (${pendingCount})` },
            { key: 'answered', label: `Answered (${answeredCount})` },
          ].map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)} className={`px-3 py-1.5 rounded-full font-bold transition-colors ${filter === t.key ? 'bg-gold text-navy' : 'bg-white/10 text-white/80'}`} data-testid={`clr-filter-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="container mx-auto px-4 py-5 max-w-2xl">
        {loading ? (
          <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-border rounded-lg" data-testid="clr-center-empty">
            <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No clarifications yet. Raise them from the bid wizard while quoting.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(c => (
              <div key={c.id} className={`border-2 rounded-lg p-4 ${c.answer ? 'border-green-200 bg-green-50/20' : 'border-orange/30 bg-orange/5'}`} data-testid={`clr-item-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <button onClick={() => navigate(`/tender/${c.tender_id}`)} className="text-xs text-orange font-bold underline" data-testid={`clr-item-${c.id}-tender-link`}>{c.tender_title || c.tender_id}</button>
                    {c.tender_category && <span className="text-[10px] text-muted-foreground ml-2">· {c.tender_category}</span>}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold inline-flex items-center ${c.answer ? 'bg-green-100 text-green-800' : 'bg-orange/20 text-orange'}`} data-testid={`clr-item-${c.id}-status`}>
                    {c.answer ? <><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> ANSWERED</> : <><Clock className="h-2.5 w-2.5 mr-0.5" /> PENDING</>}
                  </span>
                </div>
                <div className="text-sm text-navy font-semibold" data-testid={`clr-item-${c.id}-question`}>Q: {c.question}</div>
                {c.answer && <div className="text-xs text-foreground/80 mt-2 bg-card border border-border rounded p-2" data-testid={`clr-item-${c.id}-answer`}><strong className="text-green-700">A:</strong> {c.answer}</div>}
                <div className="text-[10px] text-muted-foreground mt-2">{new Date(c.created_at).toLocaleString()} {c.is_own ? '· asked by you' : ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
